import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronRight, Zap, Crown, Gift } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import ScrollToTop from '../components/ScrollToTop';

const PRICING = [
  { name: "Free", price: "$0", period: "/forever", credits: "10,000", features: ["204+ AI Voices", "26+ Languages", "All Voice Styles", "Audio Download", "Speech to Text"], highlight: false, icon: <Gift className="w-5 h-5" />, color: "from-green-500 to-emerald-500" },
  { name: "Creator", price: "$9", period: "/month", credits: "200,000", features: ["Everything in Free", "Voice Cloning", "Priority Support", "Long-form Audio", "Commercial License"], highlight: true, icon: <Zap className="w-5 h-5" />, color: "from-blue-500 to-cyan-500" },
  { name: "Pro", price: "$29", period: "/month", credits: "1,000,000", features: ["Everything in Creator", "Custom Voices", "Dedicated Support", "Bulk Processing", "Advanced Analytics"], highlight: false, icon: <Crown className="w-5 h-5" />, color: "from-purple-500 to-pink-500" },
];

export default function PricingPage() {
  const navigate = useNavigate();

  useEffect(() => { document.title = 'Pricing | VoiceraLab'; }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <div className="pt-24 pb-20 max-w-5xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-primary text-sm uppercase tracking-widest font-medium">Simple pricing</p>
          <h1 className="text-4xl sm:text-5xl font-bold font-outfit text-foreground mt-3">
            <span className="text-gradient">Choose Your Plan</span>
          </h1>
          <p className="text-muted-foreground mt-3 max-w-lg mx-auto">Start free with 10,000 characters. Upgrade anytime for more power.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {PRICING.map((plan, i) => (
            <div key={i} className={`glass-card rounded-2xl p-6 border transition-all ${plan.highlight ? 'border-primary ring-2 ring-primary/20 scale-105' : 'border-border'}`}>
              {plan.highlight && <span className="inline-block px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold mb-3">Most Popular</span>}
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${plan.color} flex items-center justify-center text-white`}>{plan.icon}</div>
                <h3 className="font-bold font-outfit text-foreground text-xl">{plan.name}</h3>
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                <span className="text-muted-foreground text-sm">{plan.period}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">{plan.credits} characters/month</p>
              <ul className="mt-5 space-y-2.5">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm text-foreground">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/auth?mode=register')}
                className={`mt-6 w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${plan.highlight ? 'gradient-btn' : 'bg-secondary text-foreground hover:bg-secondary/70'}`}
              >
                {plan.price === "$0" ? 'Start Free' : 'Get Started'} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}
